/**
 * Cases Carousel (Alpine.js Component)
 *
 * Provides an infinite carousel/slider for case studies with navigation controls and dot pagination.
 * Implements smooth slide transitions with CSS transforms and loops infinitely.
 * Supports touch/swipe gestures for mobile navigation.
 *
 * @param {number} totalSlides - Total number of slides in the carousel
 *
 * @returns {Object} Alpine.js component object with reactive data and methods
 *
 * @example
 * // Usage in Twig template
 * x-data="casesCarousel({{ block.cases.all()|length }})"
 */
export function casesCarousel(totalSlides) {
	return {
		currentIndex: 0,
		totalSlides: totalSlides,
		touchStartX: 0,
		touchEndX: 0,
		touchStartY: 0,
		touchEndY: 0,
		isDragging: false,
		minSwipeDistance: 50,

		/**
		 * Initializes the carousel component.
		 * Sets up keyboard navigation by making the carousel focusable and listening for keydown events.
		 * Called automatically by Alpine.js when the component is mounted.
		 */
		init() {
			this.$el.setAttribute("tabindex", "0");
			this.$el.addEventListener("keydown", this.handleKeydown.bind(this));
		},

		/**
		 * Advances the carousel to the next slide.
		 * Loops back to the first slide when reaching the end (infinite carousel).
		 */
		next() {
			this.currentIndex = (this.currentIndex + 1) % this.totalSlides;
		},

		/**
		 * Moves the carousel to the previous slide.
		 * Loops to the last slide when at the beginning (infinite carousel).
		 */
		prev() {
			this.currentIndex =
				(this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
		},

		/**
		 * Navigates directly to a specific slide by index.
		 *
		 * @param {number} index - The zero-based index of the target slide
		 */
		goToSlide(index) {
			if (index >= 0 && index < this.totalSlides) {
				this.currentIndex = index;
			}
		},

		/**
		 * Handles keyboard navigation with arrow keys.
		 * Left/Up arrows navigate to previous slide, Right/Down arrows to next slide.
		 *
		 * @param {KeyboardEvent} event - The keyboard event
		 */
		handleKeydown(event) {
			if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
				event.preventDefault();
				this.prev();
			} else if (
				event.key === "ArrowRight" ||
				event.key === "ArrowDown"
			) {
				event.preventDefault();
				this.next();
			}
		},

		/**
		 * Handles the touch start event to initiate swipe detection.
		 * Captures the initial touch coordinates when the user touches the screen.
		 *
		 * @param {TouchEvent} event - The touch start event containing touch coordinates
		 */
		handleTouchStart(event) {
			this.touchStartX = event.touches[0].clientX;
			this.touchStartY = event.touches[0].clientY;
			this.isDragging = true;
		},

		/**
		 * Handles the touch move event to track finger movement.
		 * Updates the end coordinates as the user moves their finger across the screen.
		 *
		 * @param {TouchEvent} event - The touch move event containing current touch coordinates
		 */
		handleTouchMove(event) {
			if (!this.isDragging) return;

			this.touchEndX = event.touches[0].clientX;
			this.touchEndY = event.touches[0].clientY;
		},

		/**
		 * Handles the touch end event to evaluate and execute swipe gesture.
		 * Calculates the swipe direction and distance, then triggers navigation if thresholds are met.
		 * Only horizontal swipes exceeding the minimum distance and greater than vertical movement
		 * will trigger slide navigation.
		 */
		handleTouchEnd() {
			if (!this.isDragging) return;

			this.isDragging = false;

			const horizontalDistance = this.touchStartX - this.touchEndX;
			const verticalDistance = Math.abs(
				this.touchStartY - this.touchEndY,
			);

			if (
				Math.abs(horizontalDistance) > this.minSwipeDistance &&
				Math.abs(horizontalDistance) > verticalDistance
			) {
				if (horizontalDistance > 0) {
					this.next();
				} else {
					this.prev();
				}
			}

			this.touchStartX = 0;
			this.touchEndX = 0;
			this.touchStartY = 0;
			this.touchEndY = 0;
		},
	};
}
